import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../../lib/dom";
import { widgetShell, fetchWidget } from "./base";

type LanyardWidget = Extract<ProfileConfig["widgets"][number], { type: "lanyard" }>;

type LanyardEmoji = { name: string; id: string | null; animated: boolean };

type LanyardData = {
  discordStatus: "online" | "idle" | "dnd" | "offline";
  username: string;
  globalName: string | null;
  avatarHash: string | null;
  avatarAnimated: boolean;
  avatarUrl: string | null;
  decorationUrl: string | null;
  bannerUrl: string | null;
  nameplate: {
    asset: string;
    videoUrl: string;
    staticUrl: string;
    palette: string | null;
    label: string | null;
  } | null;
  nameStyle: { colors: string[]; effectId: number; fontId: number } | null;
  serverTag: { tag: string; badgeUrl: string } | null;
  publicFlags: number;
  avatarDecorationAsset: string | null;
  userId: string;
  activities: {
    type: number;
    name: string;
    state?: string;
    details?: string;
    emoji?: LanyardEmoji | null;
  }[];
  spotify: {
    song: string;
    artist: string;
    album: string;
    albumArt: string;
  } | null;
  listeningToSpotify: boolean;
};

const STATUS_LABEL: Record<string, string> = {
  online: "online",
  idle: "idle",
  dnd: "do not disturb",
  offline: "offline",
  unknown: "status unknown",
};

// localStorage cache of the last-known identity fields for a Discord user.
// Used to keep the widget looking like itself during Lanyard outages or
// transient fetch failures, rather than collapsing to a red error chip.
//
// We deliberately exclude activities and spotify — those are live-by-nature
// and showing a stale "playing Minecraft" from 2 hours ago is worse than
// showing nothing. Identity (name, avatar, server tag) doesn't go stale
// the same way; it's still the right person.
//
// Keyed by Discord user ID so multi-account use-cases don't collide, and
// versioned so we can change the shape later without parsing zombie data.
const CACHE_VERSION = 1;
const CACHE_PREFIX = "bio:lanyard:cache:";
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

type LanyardIdentityCache = {
  v: number;
  savedAt: number;
  username: string;
  globalName: string | null;
  avatarUrl: string | null;
  decorationUrl: string | null;
  bannerUrl: string | null;
  nameplate: LanyardData["nameplate"];
  nameStyle: LanyardData["nameStyle"];
  serverTag: LanyardData["serverTag"];
  userId: string;
};

function loadCache(uid: string): LanyardIdentityCache | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + uid);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LanyardIdentityCache;
    if (parsed.v !== CACHE_VERSION) return null;
    if (Date.now() - parsed.savedAt > CACHE_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCache(d: LanyardData): void {
  try {
    const entry: LanyardIdentityCache = {
      v: CACHE_VERSION,
      savedAt: Date.now(),
      username: d.username,
      globalName: d.globalName,
      avatarUrl: d.avatarUrl,
      decorationUrl: d.decorationUrl,
      bannerUrl: d.bannerUrl,
      nameplate: d.nameplate,
      nameStyle: d.nameStyle,
      serverTag: d.serverTag,
      userId: d.userId,
    };
    localStorage.setItem(CACHE_PREFIX + d.userId, JSON.stringify(entry));
  } catch {
    // Quota exceeded, privacy mode, etc. — silent failure is fine; the
    // widget still works, just without the resilience cache.
  }
}

// Synthesize a LanyardData-shaped object from cached identity fields.
// Activities/spotify are emptied, status forced to "unknown" so the
// renderer takes the same path as a live render but visibly indicates
// the data is not current.
function cacheToData(c: LanyardIdentityCache): LanyardData {
  return {
    discordStatus: "unknown" as LanyardData["discordStatus"],
    username: c.username,
    globalName: c.globalName,
    avatarHash: null,
    avatarAnimated: false,
    avatarUrl: c.avatarUrl,
    decorationUrl: c.decorationUrl,
    bannerUrl: c.bannerUrl,
    nameplate: c.nameplate,
    nameStyle: c.nameStyle,
    serverTag: c.serverTag,
    publicFlags: 0,
    avatarDecorationAsset: null,
    userId: c.userId,
    activities: [],
    spotify: null,
    listeningToSpotify: false,
  };
}

function emojiUrl(e: LanyardEmoji): string | null {
  if (!e.id) return null;
  const ext = e.animated ? "gif" : "png";
  return `https://cdn.discordapp.com/emojis/${e.id}.${ext}?size=32`;
}

export function mountLanyardWidget(cfg: LanyardWidget): HTMLElement {
  const shell = widgetShell({ kind: "lanyard", title: "Discord" });
  shell.setLoading();

  const controller = new AbortController();
  shell.root.addEventListener("bio:teardown", () => controller.abort());
  const hide = new Set(cfg.hide ?? []);
  // Track whether we've ever successfully rendered. If yes, transient
  // errors on later periodic refreshes keep the old UI visible instead
  // of nuking it back to a red error chip.
  let hasRendered = false;

  const render = async () => {
    const env = await fetchWidget<LanyardData>(
      "lanyard",
      { uid: cfg.discordUserId },
      { signal: controller.signal },
    );
    if (!env.ok || !env.data) {
      // No live data. If we've already rendered once this session, leave
      // the old UI in place (existing behavior). Otherwise, try the
      // identity cache — better to show "enahs (status unknown)" with
      // their real avatar than a red error chip during a Lanyard outage.
      if (hasRendered) return;
      const cached = loadCache(cfg.discordUserId);
      if (cached) {
        renderData(cacheToData(cached), { stale: true });
        // Do NOT set hasRendered = true; if the next poll succeeds we
        // want the live render to fully take over (including activities
        // and spotify, which the stale path suppresses).
        return;
      }
      shell.setError(env.error ?? "unavailable");
      return;
    }
    hasRendered = true;
    const d = env.data;
    saveCache(d);
    renderData(d, { stale: false });
  };

  // Pulled out of the fetch handler so the cache-fallback path can call
  // it with a synthesized LanyardData. `stale` flips two behaviors:
  //   1. Skip activity/spotify rendering (those would be stale lies)
  //   2. Render the status as "status unknown" with a muted indicator
  const renderData = (d: LanyardData, { stale }: { stale: boolean }) => {
    shell.body.innerHTML = "";

    // Avatar with optional decoration overlay.
    const avatar = el("div", {
      class: "widget-lanyard__avatar",
      "data-status": d.discordStatus,
    });
    if (d.avatarUrl) {
      avatar.append(
        el("img", {
          class: "widget-lanyard__avatar-img",
          src: d.avatarUrl,
          alt: "",
          draggable: false,
        }),
      );
    }
    if (d.decorationUrl && !hide.has("decoration")) {
      avatar.append(
        el("img", {
          class: "widget-lanyard__avatar-deco",
          src: d.decorationUrl,
          alt: "",
          draggable: false,
          "aria-hidden": "true",
        }),
      );
    }

    const info = el("div", { class: "widget-lanyard__info" });

    // Name row: display name + optional server tag chip.
    const nameRow = el("div", { class: "widget-lanyard__name-row" });
    const name = el("div", { class: "widget-lanyard__name" }, [
      d.globalName || d.username,
    ]);
    // Apply Nitro name-effect gradient if present.
    if (d.nameStyle && d.nameStyle.colors.length > 0 && !hide.has("nameEffect")) {
      const stops = d.nameStyle.colors.join(", ");
      name.style.backgroundImage = `linear-gradient(135deg, ${stops})`;
      name.classList.add("widget-lanyard__name--gradient");
    }
    nameRow.append(name);
    if (d.serverTag && !hide.has("serverTag")) {
      const tag = el("span", { class: "widget-lanyard__server-tag" }, [
        el("img", { src: d.serverTag.badgeUrl, alt: "", draggable: false }),
        el("span", {}, [d.serverTag.tag]),
      ]);
      nameRow.append(tag);
    }
    info.append(nameRow);

    // Status line.
    if (!hide.has("status")) {
      if (stale) {
        // Cache-fallback path. Skip activity/spotify entirely — those
        // are live-by-nature and would be misleading from a cache.
        info.append(
          el(
            "div",
            { class: "widget-lanyard__status widget-lanyard__status--idle" },
            [STATUS_LABEL.unknown],
          ),
        );
      } else if (d.listeningToSpotify && d.spotify) {
        info.append(
          el("div", {
            class: "widget-lanyard__status widget-lanyard__status--spotify",
          }, [
            el("span", { class: "widget-lanyard__status-icon" }, ["♫"]),
            el("span", { class: "widget-lanyard__status-text" }, [
              `${d.spotify.song} — ${d.spotify.artist}`,
            ]),
          ]),
        );
      } else {
        const custom = d.activities.find((x) => x.type === 4);
        if (custom && (custom.state || custom.emoji)) {
          const statusEl = el("div", {
            class: "widget-lanyard__status widget-lanyard__status--custom",
          });
          if (custom.emoji) {
            const url = emojiUrl(custom.emoji);
            if (url) {
              statusEl.append(
                el("img", {
                  class: "widget-lanyard__status-emoji",
                  src: url,
                  alt: custom.emoji.name,
                  draggable: false,
                }),
              );
            } else {
              statusEl.append(
                el("span", { class: "widget-lanyard__status-icon" }, [
                  custom.emoji.name,
                ]),
              );
            }
          }
          const text =
            custom.state || STATUS_LABEL[d.discordStatus] || d.discordStatus;
          statusEl.append(
            el("span", { class: "widget-lanyard__status-text" }, [text]),
          );
          info.append(statusEl);
        } else {
          const game = d.activities.find((x) => x.type === 0);
          if (game) {
            info.append(
              el("div", { class: "widget-lanyard__status" }, [
                el("span", { class: "widget-lanyard__status-icon" }, ["🎮"]),
                el("span", { class: "widget-lanyard__status-text" }, [game.name]),
              ]),
            );
          } else {
            info.append(
              el(
                "div",
                { class: "widget-lanyard__status widget-lanyard__status--idle" },
                [STATUS_LABEL[d.discordStatus] ?? d.discordStatus],
              ),
            );
          }
        }
      }
    }

    shell.body.append(avatar, info);

    // Broadcast Discord profile data so other components (main profile
    // avatar, username, nameplate layer) can pick it up. Opt-in per field
    // via profile.avatar.syncDecorationFromDiscord, profile.syncFromDiscord, etc.
    document.dispatchEvent(
      new CustomEvent("bio:discord-profile", {
        detail: {
          userId: d.userId,
          avatarUrl: d.avatarUrl,
          decorationUrl: d.decorationUrl,
          bannerUrl: d.bannerUrl,
          globalName: d.globalName,
          username: d.username,
          nameplate: d.nameplate,
          nameStyle: d.nameStyle,
        },
      }),
    );
  };

  void render();
  const interval = window.setInterval(render, 30_000);
  shell.root.addEventListener("bio:teardown", () => window.clearInterval(interval));

  return shell.root;
}
